import { useId, useMemo, type ReactNode } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDndContext,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { planMove, type OgsmMove } from '@/features/ogsm/reorder'
import {
  reorderAnnouncements,
  siblingCollision,
} from '@/features/ogsm/reorder-dnd'
import type { OgsmBoard } from '@/types/api'

export interface BoardReorderProps {
  board: OgsmBoard
  /** Ids of the bands on screen: objectives at the root, the drilled objective's strategies below it. */
  bandIds: string[]
  onMove: (move: OgsmMove) => void
  children: ReactNode
}

const MODIFIERS = [restrictToVerticalAxis]

export interface SiblingListProps {
  /** The sibling ids in screen order. */
  ids: string[]
  children: ReactNode
}

/**
 * One sortable sibling list. While another list's node is dragged it takes no
 * drops, so the arrow keys and the pointer only ever land on the node's siblings.
 */
export function SiblingList({ ids, children }: SiblingListProps) {
  const { active } = useDndContext()
  const foreign = active !== null && !ids.includes(String(active.id))
  return (
    <SortableContext
      items={ids}
      strategy={verticalListSortingStrategy}
      disabled={{ draggable: false, droppable: foreign }}
    >
      {children}
    </SortableContext>
  )
}

/** One drag context for the board: bands and each band's rows reorder among their own siblings. */
export function BoardReorder({
  board,
  bandIds,
  onMove,
  children,
}: BoardReorderProps) {
  const id = useId()
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )
  const collisionDetection = useMemo(() => siblingCollision(board), [board])
  const accessibility = useMemo(
    () => ({
      announcements: reorderAnnouncements(board),
      screenReaderInstructions: { draggable: OGSM_COPY.reorder.instructions },
    }),
    [board]
  )

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return
    const move = planMove(board, String(active.id), String(over.id))
    if (move) onMove(move)
  }

  return (
    <DndContext
      id={id}
      sensors={sensors}
      collisionDetection={collisionDetection}
      modifiers={MODIFIERS}
      accessibility={accessibility}
      onDragEnd={handleDragEnd}
    >
      <SiblingList ids={bandIds}>{children}</SiblingList>
    </DndContext>
  )
}
